import axios from 'axios';
import qs from 'qs';
import formutils from './formutils';
export const baseUrl = window.apiroot;
const dateTimeFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
function reviver(key, value) {
    if (typeof value === 'string' && dateTimeFormat.test(value)) {
        return new Date(value);
    }
    return value;
}
/**
 * Adds a valid CSRF token to any parameters to be sent to a controller.
 *
 * @param params The API call parameters
 * @returns the same parameters enriched with a valid CSRF token
 */
async function withCsrf(params) {
    const csrf = await api.csrf();
    params[csrf.tokenName] = csrf.token;
    return params;
}
const instance = axios.create({
    baseURL: baseUrl,
    headers: {
        // 'content-type': 'application/json',
        'content-type': 'application/x-www-form-urlencoded',
        'x-requested-with': 'XMLHttpRequest',
        accept: 'application/json',
        // device: window.appmode ? 'smartphone' : undefined,
    },
    transformResponse: [
        function transformResponse(data) {
            if (typeof data === 'string') {
                try {
                    data = JSON.parse(data, reviver);
                }
                catch (e) {
                    /* Ignore */
                }
            }
            return data;
        },
    ],
    validateStatus: () => true,
});
let numberOfAjaxCAllPending = 0;
instance.interceptors.request.use((config) => {
    numberOfAjaxCAllPending++;
    document.body.setAttribute('aria-busy', 'true');
    config.paramsSerializer = (params) => qs.stringify(params, {
        arrayFormat: 'repeat',
        // serializeDate: (date: Date) => moment(date).format('YYYY-MM-DDTHH:mm:ssZ'),
    });
    return config;
});
instance.interceptors.response.use((response) => {
    numberOfAjaxCAllPending--;
    if (numberOfAjaxCAllPending === 0) {
        document.body.removeAttribute('aria-busy');
    }
    if (response.data && response.data.dataLayer) {
        for (let i in response.data.dataLayer) {
            window.dataLayer.push(response.data.dataLayer[i]);
        }
    }
    return response;
}, (error) => {
    numberOfAjaxCAllPending--;
    if (numberOfAjaxCAllPending === 0) {
        document.body.removeAttribute('aria-busy');
    }
    return Promise.reject(error);
});
// if (response.data && response.data.dataLayer) {
//   customEventBus.emit('datalayer:push', response.data.dataLayer);
// }
const api = {
    getProduct: (pid) => {
        return instance
            .get(`/ProductApi-Product`, {
            params: {
                pid: pid,
                cachekill: new Date(),
            },
        })
            .then(({ data }) => {
            return data;
        });
    },
    getProductTile: (pid) => {
        return instance
            .get(`/Api-ProductTile`, {
            params: {
                pid: pid,
            },
        })
            .then(({ data }) => {
            return data;
        });
    },
    variantPrice: (pid) => {
        return instance
            .get(`/ProductApi-VariantPrice`, {
            params: {
                pid: pid,
            },
        })
            .then(({ data }) => {
            return data.price;
        });
    },
    getProductTiles: (pids, cancelToken) => {
        return instance
            .get(`/Api-ProductTiles`, {
            params: {
                pid: pids,
            },
            cancelToken: cancelToken === null || cancelToken === void 0 ? void 0 : cancelToken.token,
        })
            .then(({ data }) => {
            return data.products || [];
        })
            .catch((thrown) => {
            if (axios.isCancel(thrown)) {
                console.log('getProductTiles Request canceled');
            }
            else {
                console.error(thrown);
            }
        });
    },
    getForm: (type, initialValues) => {
        return instance
            .get(`/FormApi-GetForm`, {
            params: {
                form: type,
                msg: window.msgdebug ? '42' : undefined,
            },
        })
            .then(({ data }) => {
            const form = formutils.flattenForm(data.form);
            if (initialValues) {
                formutils.fillInitialValues(form, initialValues);
            }
            return form;
        });
    },
    getFormAndSubmit: (form, def, formname) => {
        const converted = formutils.objToForm(form, def);
        converted.formname = formname;
        return instance.post(`/FormApi-GetForm`, qs.stringify(converted)).then(({ data }) => {
            return formutils.flattenForm(data.form);
        });
    },
    validateForm: (form, def, formname) => {
        const converted = formutils.objToForm(form, def);
        converted.formname = formname;
        return instance.post(`/FormApi-ValidateForm`, qs.stringify(converted)).then(({ data }) => {
            return formutils.flattenForm(data.form);
        });
    },
    getUserInfo: async () => {
        return instance.get('/AccountApi-UserInfo').then(({ data: { user } }) => user);
    },
    login: async (form) => {
        return instance.post(`/Account-Login`, qs.stringify(await withCsrf(form))).then(({ data }) => {
            return data;
        });
    },
    logout: async () => {
        return instance.post(`/Account-Logout`).then(({ data }) => {
            return data;
        });
    },
    forgotPasswordSendMail: async (email) => {
        return instance.post(`/AccountApi-PasswordResetRequest`, qs.stringify({ resetPasswordEmail: email })).then(({ data }) => {
            return data;
        });
    },
    forgotPasswordChange: async (form, def, token) => {
        return instance.post(`/AccountApi-SaveNewPassword?Token=${token}`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    socialDisconnect: async (social) => {
        return instance.post(`/AccountApi-SocialDisconnect`, qs.stringify(await withCsrf({ social: social }))).then(({ data }) => {
            return data;
        });
    },
    changePassword: async (form, def) => {
        return instance.post(`/AccountApi-SaveChangedPassword`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    checkNewsletterEmail: async (email) => {
        return instance
            .get('/NewsletterApi-ShowNewslettersModal', {
            params: {
                customerEmail: email,
            },
        })
            .then((resp) => ({
            success: !resp.data.error,
        }));
    },
    closeNewsletter: async (email) => {
        return instance.get('/NewsletterApi-CloseNewslettersModal').then((resp) => ({
            success: !resp.data.error,
        }));
    },
    modalNewsletterSubmitted: async () => {
        return instance.get('/NewsletterApi-ModalNewsletterSubmitted').then((resp) => ({
            success: !resp.data.error,
        }));
    },
    submitNewsletter: async (form, def) => {
        return instance.post('/NewsletterApi-SubmitNewsletters', qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then((resp) => ({
            success: !resp.data.error,
        }));
    },
    newsletteroptin: async (token) => {
        return instance
            .get(`/NewsletterApi-Optin`, {
            params: {
                token: token,
            },
        })
            .then(({ data }) => {
            return data;
        });
    },
    saveBankInfo: async (form, def) => {
        return instance.post(`/ReturnOrderApi-SaveBankInfo`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    savePaymentInstrument: async (form, def) => {
        return instance.post(`/PaymentInstruments-SavePayment`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    searchMail: async (email) => {
        return instance.post(`/CheckoutApi-SearchEmail`, qs.stringify({ email: email })).then(({ data }) => {
            return data;
        });
    },
    validateOtp: async (otp) => {
        return instance.post(`/AccountApi-ValidateOtp`, qs.stringify({ otp: otp })).then(({ data }) => {
            return data;
        });
    },
    resendOtp: async () => {
        return instance.post(`/AccountApi-ResendOtp`).then(({ data }) => {
            return data;
        });
    },
    paymentLogos: async () => {
        return instance.get('/CheckoutApi-PaymentLogos').then((resp) => resp.data.paymentmethods);
    },
    register: async (form, def) => {
        return instance.post(`/Account-SubmitRegistration`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    reportingEventStart: async (event) => {
        return instance.get(`/ReportingEvent-Start`, {
            params: event,
        });
    },
    saveProfile: async (form, def) => {
        return instance.post(`/AccountApi-SaveProfile`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    changeEmail: async (form, def) => {
        return instance.post(`/AccountApi-ChangeEmail`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    savePrivacy: async (form, def) => {
        return instance.post(`/AccountApi-Privacy`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    getPrivacy: async () => {
        return instance.get(`/AccountApi-GetPrivacy`).then(({ data: { privacy } }) => {
            return privacy;
        });
    },
    getWishList: async () => {
        return instance.get('/WishlistApi-Get').then((resp) => resp.data.wishlist);
    },
    getCountryLanguageOptions: async () => {
        return instance.get('/AccountApi-CountryLanguage').then(({ data }) => {
            return data;
        });
    },
    getHubspotLanguagesOptions: async () => {
        return instance.get('/AccountApi-HubspotLanguages').then(({ data }) => {
            return data;
        });
    },
    getCountries: async () => {
        return instance.get('/Api-CountryList').then(({ data: { countries } }) => {
            return countries;
        });
    },
    saveCountryLanguage: async (locale) => {
        return instance
            .post('/AccountApi-SaveCountryLanguage', qs.stringify({
            locale,
        }))
            .then(({ data }) => {
            return data;
        });
    },
    addToWishlist: async (pid, quantity) => {
        return instance
            .post('/Wishlist-AddProduct', qs.stringify({
            pid,
            quantity,
        }))
            .then(({ data: { success } }) => success);
    },
    removeFromWishlist: async (pid) => {
        return instance
            .get('/Wishlist-RemoveProduct', {
            params: {
                pid,
            },
        })
            .then(({ data: { success } }) => success);
    },
    getOrders: async (numitems) => {
        return instance
            .get('/AccountApi-OrderHistory', {
            params: {
                numitems: numitems || undefined,
            },
        })
            .then((resp) => resp.data.orders);
    },
    getAftercares: async () => {
        return instance.get('/AccountApi-AftercareHistory').then((resp) => resp.data.aftercares);
    },
    getOrderDetail: async (id, token) => {
        return instance
            .get(`/OrderApi-Details`, {
            params: {
                orderID: id,
                token: token || undefined,
            },
        })
            .then((resp) => resp.data.order);
    },
    createAccountByOrder: async (form, def, ID, UUID) => {
        return instance
            .post(`/Order-CreateAccount?ID=${ID}&UUID=${UUID}`, qs.stringify(await withCsrf(formutils.objToForm(form, def))))
            .then(({ data }) => { return data; });
    },
    checkPaybylinkStatus: async (id, token) => {
        return instance
            .get(`/Paybylink-CheckPayment`, {
            params: {
                orderID: id,
                token: token || undefined,
            },
        })
            .then((resp) => resp.data);
    },
    checkPaymentStatus: async () => {
        return instance.get(`/CheckoutApi-CheckPaymentStatus`, {}).then((resp) => resp.data);
    },
    getReturns: async () => {
        return instance.get('/AccountApi-ReturnHistory').then((resp) => resp.data.returns);
    },
    getReturnDetail: async (id, token) => {
        return instance
            .get('/ReturnOrderApi-Details', {
            params: {
                orderID: id,
                token: token || undefined,
            },
        })
            .then((resp) => resp.data.returncase);
    },
    uploadFile: (fieldname, file, onUploadProgress) => {
        let formData = new FormData();
        formData.append(fieldname, file);
        return instance
            .post('/FileUpload-Post', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress,
        })
            .then(({ data }) => data);
    },
    removeFile: (fieldname, file) => {
        return instance
            .post('FileUpload-RemoveFile', qs.stringify({
            field: fieldname,
            file: file,
        }))
            .then(({ data }) => data);
    },
    cancelOrder: async (orderNumber, token) => {
        return instance
            .post('OrderApi-CancelOrder', qs.stringify({
            orderNumber,
            token,
        }))
            .then(({ data }) => data);
    },
    openReturn: async (openReturnOrderNumber, openReturnOrderEmail) => {
        return instance
            .post('ReturnOrderApi-OpenReturn', qs.stringify({
            openReturnOrderNumber,
            openReturnOrderEmail,
        }))
            .then(({ data }) => data);
    },
    trackOrderOrReturn: async (form, def) => {
        return instance.post('OrderApi-TrackOrderOrReturn', qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => data);
    },
    trackOrder: async (form, def) => {
        return instance.post('OrderApi-TrackOrder', qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => data);
    },
    getReturnReasons: async () => {
        return instance.get('ReturnOrderApi-ReturnReasons').then(({ data }) => data.returnReasons);
    },
    createReturnRequest: async (ordernumber, returnRequest) => {
        return instance
            .post(`OrderApi-Return?ordernumber=${ordernumber}`, returnRequest, {
            headers: {
                'content-type': 'application/json',
                accept: 'application/json',
            },
            validateStatus: () => true, // also return details in case of http500
        })
            .then(({ data }) => data);
    },
    expressGooglepayAuthorize: async (data) => {
        return instance
            .post('CheckoutExpressGooglePay-GooglepayAuthorize', data, {
            headers: {
                'content-type': 'application/json',
                accept: 'application/json',
            },
            validateStatus: () => true, // also return details in case of http500
        })
            .then(({ data }) => data.data);
    },
    expressGooglepayDataChange: async (data) => {
        return instance
            .post('CheckoutExpressGooglePay-GooglepayDataChange', data, {
            headers: {
                'content-type': 'application/json',
                accept: 'application/json',
            },
            validateStatus: () => true, // also return details in case of http500
        })
            .then(({ data }) => data.data);
    },
    expressAmazonPayShopperDetails: async (data) => {
        return instance
            .post('CheckoutExpressAmazonPay-ShopperDetails', data, {
            headers: {
                'content-type': 'application/json',
                accept: 'application/json',
            },
            validateStatus: () => true, // also return details in case of http500
        })
            .then(({ data }) => data.data);
    },
    expressSubmitPayment: async (form, def) => {
        return instance.post(`/CheckoutServices-SubmitExpress`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    createExchangeRequest: async (returnRequest) => {
        return instance
            .post('ExchangeOrderApi-CreateExchange', returnRequest, {
            headers: {
                'content-type': 'application/json',
                accept: 'application/json',
            },
            validateStatus: () => true, // also return details in case of http500
        })
            .then(({ data }) => data);
    },
    cancelExchangeRequest: async () => {
        return instance.post(`/ExchangeOrderApi-Cancel`, qs.stringify(await withCsrf({}))).then(({ data }) => {
            return data;
        });
    },
    getAfterCares: async () => {
        return instance.get('/AccountApi-OrderHistory').then((resp) => resp.data.orders);
    },
    csrf: async () => {
        const { data } = await instance.post('CSRF-Generate');
        return data.csrf;
    },
    getMiniCart: async () => {
        return instance.get('CartApi-MiniCart').then(({ data }) => {
            return data;
        });
    },
    getFullCart: async () => {
        return instance.get('CartApi-Cart').then(({ data }) => {
            return data;
        });
    },
    emailMyCart: async (form, def) => {
        return instance.post(`/CartApi-MailMyCart`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    expressCheckoutStart: async () => {
        return instance.post('CheckoutExpress-Start').then(({ data }) => {
            return data;
        });
    },
    getCheckoutData: async () => {
        return instance.get('CheckoutApi-CheckoutData').then(({ data }) => {
            return data;
        });
    },
    getConnectivityData: async () => {
        return instance.get('CheckoutApi-ConnectivityData').then(({ data }) => {
            return data;
        });
    },
    getHolidayCalendar: async () => {
        return instance.get('CheckoutApi-Holidays').then(({ data }) => {
            return data.data;
        });
    },
    getAdyenConfig: async () => {
        return instance.get('CheckoutApi-AdyenConfig').then(({ data }) => {
            return data;
        });
    },
    getAddressBook: async () => {
        return instance.get('AddressApi-AddressBook').then(({ data }) => {
            return data;
        });
    },
    getAddress: async (id) => {
        return instance.get('AddressApi-Address', { params: { addressId: id } }).then(({ data }) => {
            return data;
        });
    },
    getProfile: async () => {
        return instance.get('AccountApi-Profile').then(({ data }) => {
            return data;
        });
    },
    getAccountModel: async () => {
        return instance.get('AccountApi-AccountModel').then(({ data }) => {
            return data;
        });
    },
    addToBag: (pid, quantity, replaced) => {
        return instance
            .post(`/CartApi-AddProduct`, qs.stringify({
            pid: pid,
            quantity: quantity || 1,
            options: [],
            replaced: replaced,
        }))
            .then(({ data }) => {
            return data;
        });
    },
    addPromoCode: async (code) => {
        return instance.get(`/Cart-AddCoupon`, { params: await withCsrf({ couponCode: code }) }).then(({ data }) => data);
    },
    promocodeRemove: async (uuid) => {
        return instance.get(`/Cart-RemoveCouponLineItem`, { params: await withCsrf({ uuid: uuid }) }).then(({ data }) => data);
    },
    removeFromBag: (pid, uuid) => {
        return instance.post(`/CartApi-RemoveProductLineItem`, qs.stringify({ pid, uuid })).then(({ data }) => data);
    },
    checkoutUpdateShippingAddress: async (form, def) => {
        return instance.post(`/CheckoutApi-UpdateShippingAddress`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data.order;
        });
    },
    checkoutUpdateBillingAddress: async (form, def) => {
        return instance.post(`/CheckoutApi-UpdateBillingAddress`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data.order;
        });
    },
    checkoutClearShippingAddress: async (form, def) => {
        return instance.post(`/CheckoutApi-ClearShippingAddress`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data.order;
        });
    },
    checkoutClearActivateAddress: async (form, def) => {
        return instance.post(`/CheckoutApi-ClearActivationAddress`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data.order;
        });
    },
    checkoutUpdateActivationAddress: async (form, def) => {
        return instance.post(`/CheckoutApi-UpdateActivationAddress`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data.order;
        });
    },
    checkoutSubmitPackaging: async (form, def) => {
        return instance.post(`/CheckoutApi-SubmitPackaging`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    checkoutSubmitShipping: async (form, def) => {
        return instance.post(`/CheckoutShippingServices-SubmitShipping`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    checkoutSubmitActivation: async (form, def) => {
        return instance.post(`/CheckoutShippingServices-SubmitActivation`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    checkoutSubmitPet: async (form, def) => {
        return instance.post(`/CheckoutShippingServices-SubmitPet`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    checkoutSubmitShippingPickup: async (form, def) => {
        return instance.post(`/CheckoutShippingServicesPickup-SubmitShipping`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    checkoutSubmitBilling: async (form, def, selectedPaymentMethod) => {
        return instance.post(`/CheckoutServices-SubmitBilling`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    checkoutSaveMail: async (email) => {
        return instance
            .post(`/CheckoutApi-SaveMail`, qs.stringify(await withCsrf({
            email: email,
        })))
            .then(({ data }) => {
            return data;
        });
    },
    checkoutRemoveMail: async () => {
        return instance.post(`/CheckoutApi-RemoveMail`).then(({ data }) => {
            return data;
        });
    },
    checkoutPlaceOrder: async (form, def, selectedPaymentMethod, nonce, policies) => {
        return instance.post(`/CheckoutServices-PlaceOrder`, qs.stringify(await withCsrf(formutils.objToForm(form, def))), {
            params: {
                nexiPaymentMethod: selectedPaymentMethod,
                nonce: nonce ? JSON.stringify(nonce) : null,
                policies: policies ? JSON.stringify(policies) : null
            }
        }).then(({ data }) => {
            return data;
        });
    },
    paypalExpressPlaceOrder: async (orderNumber, token, shopperEmail) => {
        return instance
            .post(`/CheckoutExpress-PaypalExpressPlaceOrder`, qs.stringify(await withCsrf({
            orderNumber: orderNumber,
            token: token,
            shopperEmail: shopperEmail,
        })))
            .then(({ data }) => {
            return data;
        });
    },
    saveAddress: async (form, def, id) => {
        return instance
            .post('/AddressApi-SaveAddress', qs.stringify(await withCsrf(formutils.objToForm(form, def))), {
            params: {
                addressId: id,
            },
        })
            .then(({ data }) => {
            return data;
        });
    },
    saveAddressNoId: async (form, def) => {
        return instance
            .post('/AddressApi-SaveAddressNoId', qs.stringify(await withCsrf({})))
            .then(({ data }) => {
            return data;
        });
    },
    deleteAddress: async (id, isDefault) => {
        return instance
            .get(`/Address-DeleteAddress`, {
            params: {
                addressId: id,
                isDefault: isDefault,
            },
        })
            .then(({ data }) => {
            return data;
        });
    },
    editAddress: (id, initialValues) => {
        return instance
            .get(`/AddressApi-EditAddress`, {
            params: {
                addressId: id,
            },
        })
            .then(({ data }) => {
            const form = formutils.flattenForm(data.form);
            if (initialValues) {
                formutils.fillInitialValues(form, initialValues);
            }
            return form;
        });
    },
    checkBoutiqueAvailability: (pid, country, excludemode, geo) => {
        return instance
            .get(`/BoutiqueApi-CheckAvailability`, {
            params: {
                productid: pid,
                country,
                exclude: !!excludemode,
                lat: geo === null || geo === void 0 ? void 0 : geo.lat,
                lng: geo === null || geo === void 0 ? void 0 : geo.lng,
            },
        })
            .then(({ data }) => {
            return data;
        })
            .catch((thrown) => {
            console.error(thrown);
        });
    },
    getPaymentMethods: () => {
        return instance.get(`/Adyen-GetPaymentMethods`).then(({ data }) => {
            return data;
        });
    },
    adyenPaymentFromComponent: (data, paymentMethod) => {
        return instance.post(`/Adyen-PaymentFromComponent`, qs.stringify({ data: JSON.stringify(data), paymentMethod: paymentMethod })).then(({ data }) => {
            return data;
        });
    },
    adyenPaymentDetails: (data) => {
        return instance.post(`/Adyen-PaymentDetails`, qs.stringify({ data: JSON.stringify(data) })).then(({ data }) => {
            return data.data;
        });
    },
    reserveInBoutique: async (form, def) => {
        return instance.post(`/BoutiqueApi-ReserveSubmit`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    bookAnAppointmentSlots: async (store, type) => {
        return instance
            .get(`/BookAnAppointmentApi-Slots`, {
            params: {
                store: store,
                type: type,
            },
        })
            .then(({ data }) => {
            return data;
        });
    },
    bookAnAppointmentSubmit: async (form, def) => {
        return instance.post(`/BookAnAppointmentApi-Submit`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data.data;
        });
    },
    bookAnAppointmentEdit: async (form, def) => {
        return instance.post(`/BookAnAppointmentApi-Edit`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data.data;
        });
    },
    bookAnAppointmentTrack: async (form, def) => {
        return instance.post(`/BookAnAppointmentApi-Track`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    bookAnAppointmentCancel: async (code) => {
        return instance.post(`/BookAnAppointmentApi-CancelAppointment`, qs.stringify(await withCsrf({ code: code }))).then(({ data }) => {
            return data;
        });
    },
    bookAnAppointmentGet: async (code) => {
        return instance
            .get(`/BookAnAppointmentApi-Get`, {
            params: {
                code: code,
            },
        })
            .then(({ data }) => {
            return data;
        });
    },
    bookAnAppointmentList: async () => {
        return instance.get(`/BookAnAppointmentApi-List`).then(({ data }) => {
            return data.data;
        });
    },
    notifymeSubmit: async (form, def) => {
        return instance.post(`/NotifyMe-NotifySubmit`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    notifymeWishlist: async (form, def) => {
        return instance.post(`/NotifyMe-NotifySubmitWishlist`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    contactUsSubmit: async (form, def) => {
        return instance.post(`/CustomerCareApi-SubmitContactForm`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    contactUsFaqs: async (id) => {
        return instance
            .get(`/CustomerCareApi-ContactusFaqs`, {
            params: {
                id: id,
            },
        })
            .then(({ data }) => {
            return data.items;
        });
    },
    getBoutiqueCountries: () => {
        return instance.get(`/BoutiqueApi-Countries`).then(({ data }) => {
            return data.data;
        });
    },
    getBoutiqueCountriesAndCities: () => {
        return instance.get(`/BoutiqueApi-CountriesAndCities`).then(({ data }) => {
            return data.data;
        });
    },
    // slotid=einstein-product-recommendations
    getSlotConfig: (slotid, cancelToken) => {
        return instance
            .get(`/__SYSTEM__Slot-Request`, {
            params: {
                slotid: slotid,
                context: 'global',
            },
            cancelToken: cancelToken.token,
        })
            .then((data) => data)
            .catch((thrown) => {
            if (axios.isCancel(thrown)) {
                console.log('getSlotConfig Request canceled');
            }
            else {
                console.error(thrown);
            }
        });
    },
    getSlotContent: (slotuuid, pid) => {
        return instance
            .post(`/CQRecomm-Start`, qs.stringify({
            auid: '',
            scid: slotuuid,
            pid0: pid,
        }))
            .then((data) => data.data);
    },
    searchProducts: (params) => {
        let queryparams = {
            q: params.q || undefined,
            cgid: params.id || undefined,
            srule: params.selectedSort || undefined,
            sz: params.pageSize || undefined,
            start: params.start ? Math.max(params.start - 1, 0) : undefined,
            type: params.type || undefined,
            shelf: params.shelf || undefined,
        };
        if (params.selectedFilters) {
            params.selectedFilters
                .filter((value) => value === null || value === void 0 ? void 0 : value.values.length)
                .forEach((currentValue, index) => {
                queryparams[`prefn${index + 1}`] = currentValue.id;
                queryparams[`prefv${index + 1}`] = currentValue.values.join('|');
            });
        }
        // console.log('API slug is ' + params.slug);
        let url = '/SearchApi-Search';
        if (params.slug && !params.id) {
            url = `https://${window.location.host}${params.slug}`;
            queryparams.json = true;
        }
        return instance
            .get(url, { params: queryparams, cancelToken: params === null || params === void 0 ? void 0 : params.cancelToken })
            .then(({ data }) => data.data)
            .catch((thrown) => {
            if (axios.isCancel(thrown)) {
                // console.log('searchProducts Request canceled');
            }
            else {
                console.error(thrown);
            }
        });
    },
    getSuggestions: (params) => {
        return instance
            .get('/SearchApi-GetSuggestions', { params: { q: params === null || params === void 0 ? void 0 : params.q } })
            .then(({ data }) => data)
            .catch((thrown) => {
            if (axios.isCancel(thrown)) {
                // console.log('GetSuggestions Request canceled');
            }
            else {
                console.error(thrown);
            }
        });
    },
    getSizeChart: (pid) => {
        return instance
            .get(`/ProductApi-SizeChart`, {
            params: {
                pid: pid,
            },
        })
            .then(({ data }) => {
            return data;
        });
    },
    getStoresForPickup: () => {
        return instance.get('/StoresApi-PickupStores').then(({ data }) => {
            return data.stores;
        });
    },
    getStoresForAppointment: () => {
        return instance.get('/StoresApi-StoresForAppointment').then(({ data }) => {
            return data.data;
        });
    },
    searchStores: (from) => {
        //@ts-ignore
        return instance.get(`/StoresApi-FindAll`, { params: from }).then(({ data }) => {
            return data;
        });
    },
    searchStoreDetails: (storeId) => {
        //@ts-ignore
        return instance.get(`/StoresApi-StoreDetails`, { params: { storeId } }).then(({ data }) => {
            return data.store;
        });
    },
    countrySelectorHTML: () => {
        return instance.get('/Page-Locale', {
            params: {
                modal: true,
            },
        });
    },
    countrySelectorJson: () => {
        return instance.get('/Page-LocaleJson').then(({ data }) => {
            return data.data;
        });
    },
    languageSelectorJson: () => {
        return instance.get('/Page-LanguagesJson').then(({ data }) => {
            return data.data;
        });
    },
    getCustomerPaymentInstruments: async () => {
        return instance.get(`/AccountApi-PaymentInstruments`).then(({ data }) => {
            return data;
        });
    },
    getPreferredBoutique: async () => {
        return instance.get(`/AccountApi-PreferredBoutique`).then(({ data }) => {
            return data;
        });
    },
    deletePaymentMethod: async (id) => {
        return instance
            .get(`/PaymentInstruments-DeletePayment`, {
            params: {
                UUID: id,
            },
        })
            .then(({ data }) => {
            return data;
        });
    },
    searchFaqs: async (query) => {
        return instance.get('/ClientServiceApi-FaqSearch', { params: { query: query } }).then(({ data }) => {
            return data;
        });
    },
    /**
     * Gets page data in JSON format from any Page Designer page given the respective ID
     *
     * @param pid page ID
     */
    getPageJson: async (pid) => {
        return instance
            .get(`/Api-Page`, {
            params: {
                pid,
            },
        })
            .then(({ data }) => {
            return data;
        });
    },
    allowTracking: async () => {
        return instance
            .get(`/ConsentTracking-SetSession`, {
            params: {
                consent: true,
            },
        })
            .then(({ data }) => {
            return data;
        });
    },
    allowPersonalizedTracking: async (data) => {
        return instance
            .post(`/ConsentTracking-SetSessionCustomCookies`, data, {
            params: {
                consent: true,
            },
        })
            .then(({ data }) => {
            return data;
        });
    },
    /**
     * Gets site id
     */
    getSiteId: async () => {
        return instance.get(`/Api-Currentsite`).then(({ data }) => {
            return data;
        });
    },
    CcpaSubmit: async (form, def) => {
        return instance.post(`/CcpaApi-Submit`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    LinkExistingCard: async (form, def) => {
        return instance.post(`/AccountApi-LinkExistingCard`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    GetLinkem: async (lat, lng) => {
        return instance.get('/Coverage-GetLinkem', {
            params: {
                lat: lat,
                lon: lng
            }
        })
            .then(({ data }) => data);
    },
    CheckAddress: async (city, address) => {
        return instance.get('/Coverage-CheckAddress', {
            params: {
                city: city,
                address: address
            }
        })
            .then(({ data }) => data);
    },
    VerifyCoverage: async (form) => {
        return instance.post('/Coverage-VerifyCoverage', qs.stringify(await withCsrf(form))).then(({ data }) => {
            return data;
        });
    },
    ConnectivityCreate: async (placeObj) => {
        return instance.post('/Coverage-Create', qs.stringify(placeObj))
            .then(({ data }) => data);
    },
    connectivitySubmitRegistry: async (form, def) => {
        return instance.post(`/Coverage-SubmitRegistry`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    connectivitySubmitPayment: async (form, def) => {
        return instance.post(`/Coverage-SubmitPayment`, qs.stringify(await withCsrf(formutils.objToForm(form, def)))).then(({ data }) => {
            return data;
        });
    },
    connectivityPlaceOrder: async (form, def, policies) => {
        return instance.post(`/Coverage-PlaceOrder`, qs.stringify(await withCsrf(formutils.objToForm(form, def))), {
            params: {
                policies: policies ? JSON.stringify(policies) : null
            }
        }).then(({ data }) => {
            return data;
        });
    },
    logAPI: async (request, response) => {
        return instance.post(`/Logger-Log`, qs.stringify(await withCsrf({})), {
            params: {
                log: 'REQUEST:\n' + request + '\nRESPONSE:\n' + response
            }
        });
    }
};
export default api;
